import {
  GetViolationsQuery,
  GetViolationsQueryVariables,
  ViolationsFilterInput,
  useMarkActiveMutation,
  useMarkIgnoredMutation,
  useMarkSuspendedMutation,
  useMarkUnderReviewMutation,
} from '@/api/rentalscape/__generated_types__/apollo';
import { ViolationStatus } from '@/api/rentalscape/__generated_types__/graphql';
import VIOLATIONS_QUERY from '@/api/rentalscape/queries/violations-query';
import { apiDomainContext } from '@/utils/gqlapi';
import { usePropertyDeckardId } from '@/utils/hooks/usePropertyDeckardId';
import { toaster } from '@/utils/toaster';
import { trackError } from '@/utils/track-error';
import { ApolloCache, useApolloClient, useQuery } from '@apollo/client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ITEMS_PER_PAGE } from './constants';
import { isDefined } from './utils/isDefined';

interface UseViolationsManagerProps {
  /**
   * The ID of the region to fetch violations for
   */
  regionId: string;
}

/**
 * Hook that provides a server-side datasource for AG Grid to fetch and display violations
 *
 * This hook handles:
 * - Fetching violations data from the GraphQL API
 * - Pagination and filtering
 */
export function useViolationsManager({ regionId }: UseViolationsManagerProps) {
  // Page number is 1-indexed
  const [page, setPage] = useState(1);

  const { propertyDeckardId } = usePropertyDeckardId();

  const [filters, setFilters] = useState<ViolationsFilterInput>({
    rawTypes: [],
    statuses: [
      ViolationStatus.Active,
      ViolationStatus.Ignored,
      ViolationStatus.Suspended,
      ViolationStatus.UnderReview,
      ViolationStatus.Unknown,
    ],
    propertyDeckardId,
  });

  /**
   * Keeps track of the end cursor for each page
   * This is used to fetch the next page of data
   * The key is the page number (1-indexed)
   */
  const endCursorRef = useRef<Record<number, string>>({});

  // Get the cursor for the current page
  const after = page > 1 ? endCursorRef.current[page - 1] : null;

  const [markActive] = useMarkActiveMutation();
  const [markIgnored] = useMarkIgnoredMutation();
  const [markSuspended] = useMarkSuspendedMutation();
  const [markUnderReview] = useMarkUnderReviewMutation();

  const queryVariables: GetViolationsQueryVariables = useMemo(
    () => ({
      regionId,
      filter: filters,
      first: ITEMS_PER_PAGE,
      after,
    }),
    [regionId, filters, after]
  );

  const {
    data: queryData,
    loading,
    refetch,
  } = useQuery<GetViolationsQuery, GetViolationsQueryVariables>(
    VIOLATIONS_QUERY,
    {
      variables: queryVariables,
      context: apiDomainContext,
      onError: (error) => {
        trackError(error);
        toaster.error('Error occurred while fetching violations.');
      },
    }
  );

  // Store the end cursor for pagination
  useEffect(() => {
    if (queryData?.region?.violations.pageInfo?.endCursor) {
      endCursorRef.current[page] =
        queryData.region.violations.pageInfo.endCursor;
    }
  }, [queryData, page]);

  // node are nullable so filtering them out
  const data = useMemo(
    () =>
      queryData?.region?.violations.edges
        .map((edge) => edge.node)
        .filter(isDefined) || [],
    [queryData]
  );

  // Helper function to update cache for a specific violation
  const updateViolationStatusInCache = useCallback(
    (
      cache: ApolloCache<GetViolationsQuery>,
      violationId: string,
      newStatus: ViolationStatus
    ) => {
      // Try to read from the cache
      const data = cache.readQuery<GetViolationsQuery>({
        query: VIOLATIONS_QUERY,
        variables: queryVariables,
      });

      // If we have data, update it
      if (data?.region?.violations.edges) {
        const edges = data.region.violations.edges;

        const updatedEdges = edges.map((edge) => {
          if (edge.node?.id === violationId) {
            return {
              ...edge,
              node: {
                ...edge.node,
                status: newStatus,
                modified: new Date().toISOString(),
              },
            };
          }
          return edge;
        });

        // Update the cache with new data
        cache.writeQuery<GetViolationsQuery>({
          query: VIOLATIONS_QUERY,
          variables: queryVariables,
          data: {
            ...data,
            region: {
              ...data.region,
              violations: {
                ...data.region.violations,
                edges: updatedEdges,
              },
            },
          },
        });
      }
    },
    [queryVariables]
  );

  const client = useApolloClient();

  const onStatusChange = useCallback(
    async (violationId: string, newStatus: ViolationStatus) => {
      // Store the original status before modification to revert if needed
      const originalViolation = data.find(
        (violation) => violation.id === violationId
      );
      if (!originalViolation) {
        trackError(new Error('Could not find violation to update'));
        toaster.error('Could not find violation to update');
        return;
      }
      const originalStatus = originalViolation.status;

      const mutationOptions = {
        variables: { violationId },
        context: apiDomainContext,
        update: (cache: ApolloCache<GetViolationsQuery>) => {
          updateViolationStatusInCache(cache, violationId, newStatus);
        },
      };

      try {
        switch (newStatus) {
          case ViolationStatus.Active:
            await markActive({
              ...mutationOptions,
              optimisticResponse: {
                __typename: 'Mutation',
                markActive: true,
              },
            });
            break;
          case ViolationStatus.Ignored:
            await markIgnored({
              ...mutationOptions,
              optimisticResponse: {
                __typename: 'Mutation',
                markIgnored: true,
              },
            });
            break;
          case ViolationStatus.Suspended:
            await markSuspended({
              ...mutationOptions,
              optimisticResponse: {
                __typename: 'Mutation',
                markSuspended: true,
              },
            });
            break;
          case ViolationStatus.UnderReview:
            await markUnderReview({
              ...mutationOptions,
              optimisticResponse: {
                __typename: 'Mutation',
                markUnderReview: true,
              },
            });
            break;
          default:
            console.error(`Unsupported status transition to ${newStatus}`);
            return;
        }
        toaster.success('Violation status update has been queued.');
      } catch (error) {
        trackError(error);
        toaster.error('Error occurred while updating violation status.');

        // Revert the cache to original status
        updateViolationStatusInCache(client.cache, violationId, originalStatus);
      }
    },
    [
      markActive,
      markIgnored,
      markSuspended,
      markUnderReview,
      updateViolationStatusInCache,
      data,
      client,
    ]
  );

  return {
    data,
    page,
    setPage,
    totalRows: 0,
    filters,
    setFilters,
    onStatusChange,
    loading,
    refetch,
  };
}
